import { api } from '@/core/lib/api'
import {
  buildAdminListQuery,
  type TAdminListParams,
} from '@/core/lib/table-query'

import type {
  TCreateTournamentRequest,
  TTournamentContext,
  TTournamentDetail,
  TTournamentListResponse,
  TTournamentModeratorOption,
  TUpdateTournamentRequest,
} from '@/modules/tournament/tournament.types'

const base = '/admin/tournaments'

export async function listTournaments(
  params: TAdminListParams
): Promise<TTournamentListResponse> {
  const response = await api.get<TTournamentListResponse>(
    `${base}?${buildAdminListQuery(params)}`
  )
  return response.data
}

export async function getTournament(id: string): Promise<TTournamentDetail> {
  const response = await api.get<TTournamentDetail>(`${base}/${id}`)
  return response.data
}

export async function createTournament(
  body: TCreateTournamentRequest
): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(base, body)
  return response.data
}

export async function updateTournament(
  id: string,
  body: TUpdateTournamentRequest
): Promise<TTournamentDetail> {
  const response = await api.patch<TTournamentDetail>(`${base}/${id}`, body)
  return response.data
}

export async function deleteTournament(id: string): Promise<void> {
  await api.delete(`${base}/${id}`)
}

export async function activateTournamentMatch(
  tournamentId: string,
  matchId: string
): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(
    `${base}/${tournamentId}/matches/${matchId}/activate`
  )
  return response.data
}

export async function setTournamentMatchAutoStart(
  tournamentId: string,
  matchId: string,
  enabled: boolean
): Promise<TTournamentDetail> {
  const response = await api.patch<TTournamentDetail>(
    `${base}/${tournamentId}/matches/${matchId}/auto-start`,
    { auto_start_enabled: enabled }
  )
  return response.data
}

export async function assignTournamentSlot(
  tournamentId: string,
  matchId: string,
  body: { slot: 1 | 2; team_id: string }
): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(
    `${base}/${tournamentId}/matches/${matchId}/assign-slot`,
    body
  )
  return response.data
}

export async function setTournamentFinalWinner(
  tournamentId: string,
  matchId: string,
  body: { team_id: string }
): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(
    `${base}/${tournamentId}/matches/${matchId}/final-winner`,
    body
  )
  return response.data
}

export async function setTournamentMatchWalkover(
  tournamentId: string,
  matchId: string,
  winnerTeamId: string
): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(
    `${base}/${tournamentId}/matches/${matchId}/walkover`,
    { winner_team_id: winnerTeamId }
  )
  return response.data
}

export async function startTournament(id: string): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(`${base}/${id}/start`)
  return response.data
}

export async function finishTournament(id: string): Promise<TTournamentDetail> {
  const response = await api.post<TTournamentDetail>(`${base}/${id}/finish`)
  return response.data
}

export async function listModeratorOptions(): Promise<
  TTournamentModeratorOption[]
> {
  const response = await api.get<TTournamentModeratorOption[]>(
    `${base}/moderator-options`
  )
  return response.data
}

export async function getTournamentContext(): Promise<TTournamentContext> {
  const response = await api.get<TTournamentContext>(`${base}/context`)
  return response.data
}
